import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

function PromoteButton({ tag }: { tag: string }) {
  const queryClient = useQueryClient();
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);
  const promote = useMutation({
    mutationFn: (t: string) => api.promoteTheme(t), // explicit arg — no stale closure
    onSuccess: () => {
      setDone(true);
      setFailed(false);
      queryClient.invalidateQueries({ queryKey: ["updates"] }); // roadmap reflects it
    },
    onError: () => setFailed(true),
  });
  const on = done || (promote.data?.alreadyOnRoadmap ?? false);
  const label = promote.isPending ? "Adding…" : on ? "On roadmap" : failed ? "Retry" : "to roadmap";
  const color = on ? "#1f7a4d" : failed ? "#b23b3b" : "#006747";
  return (
    <button
      onClick={(e) => { e.preventDefault(); if (!on && !promote.isPending) promote.mutate(tag); }}
      disabled={on || promote.isPending}
      title={on ? "On the roadmap" : failed ? "Promote failed — retry" : "Promote to roadmap"}
      style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 600, color, background: "none", border: 0, cursor: on ? "default" : "pointer", padding: 0 }}
    >
      <Icon name={on ? "check-circle" : failed ? "alert-triangle" : "lightbulb"} size={12} color={color} />
      {label}
    </button>
  );
}

const TONE: Record<string, { bar: string; label: string; chip: string }> = {
  bad: { bar: "#b3261e", label: "needs attention", chip: "#f7e4e0" },
  warn: { bar: "#9a7b1f", label: "trending", chip: "#fbf1dd" },
  ok: { bar: "#1f7a4d", label: "steady", chip: "#e8f4ee" },
};

export function Themes() {
  const { data, isLoading, error } = useQuery({ queryKey: ["themes"], queryFn: api.themes });
  if (isLoading) return <Loading />;
  if (error || !data) return <ErrorNote error={error} />;

  const max = Math.max(1, ...data.themes.map((t) => t.count));

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Feedback themes</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>
          Tickets clustered by theme across {data.totalTickets} you can see — recurring pain becomes a roadmap signal.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 14 }}>
        {data.themes.map((t) => {
          const tone = TONE[t.tone] ?? { bar: "#1f7a4d", label: "steady", chip: "#e8f4ee" };
          return (
            <div key={t.tag} style={{ background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "15px 18px" }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 15, fontWeight: 600 }}>{t.tag}</span>
                <span style={{ fontSize: 10.5, fontWeight: 600, padding: "2px 8px", borderRadius: 999, background: tone.chip, color: tone.bar }}>{tone.label}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}>
                <span style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Space Mono',monospace" }}>{t.count}</span>
                <span style={{ fontSize: 11.5, color: "#8a8e88" }}>tickets · {t.open} open{t.urgent > 0 ? ` · ${t.urgent} urgent/high` : ""}</span>
              </div>
              {/* Volume bar */}
              <div style={{ height: 5, background: "#f0efe9", borderRadius: 3, marginTop: 8, overflow: "hidden" }}>
                <div style={{ width: `${Math.round((t.count / max) * 100)}%`, height: "100%", background: tone.bar }} />
              </div>
              {/* Example tickets */}
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
                {t.examples.map((ex) => (
                  <div key={ex.ref} style={{ display: "flex", gap: 7, fontSize: 11.5 }}>
                    <span style={{ fontFamily: "'Space Mono',monospace", color: "#8a8e88", flex: "none" }}>{ex.ref}</span>
                    <span style={{ color: "#4a504a", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ex.subject}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                {t.clients.slice(0, 4).map((cl) => (
                  <span key={cl} style={{ fontSize: 10.5, color: "#16243a", background: "#e8ebf0", borderRadius: 4, padding: "1px 7px" }}>{cl}</span>
                ))}
                <div style={{ flex: 1 }} />
                <PromoteButton tag={t.tag} />
              </div>
            </div>
          );
        })}
        {data.themes.length === 0 && <div style={{ padding: 20, fontSize: 13, color: "#8a8e88" }}>No tagged tickets to cluster yet.</div>}
      </div>
    </Page>
  );
}
